<template>
	<QuestionGroup>
		<QuestionCard
			title="What type of scan would you like to run?"
		>
			<validation-provider
				#default="{ errors }"
				name="Scan Type"
				:rules="{ required: siteScan.scanType === null }"
				:value="siteScan.scanType"
			>
				<div
					class="scan-type-cards"
					data-testid="scan-type-selector-cards"
				>
					<div
						v-for="card in primaryScanTypeCards"
						:key="card.value"
						class="scan-type-card-wrapper"
					>
						<SelectorCard
							:title="card.title"
							:description="card.description"
							:model-value="siteScan.scanType === card.value"
							:disabled="!siteScannerFullAccess"
							:data-testid="`scan-type-${card.value}`"
							@input="selectScanType(card.value)"
						>
							<template #icon>
								<v-icon
									color="primary"
									size="24"
								>
									{{ getIcon(card) }}
								</v-icon>
							</template>
						</SelectorCard>
					</div>
				</div>
				<div
					v-if="errors[0]"
					class="error--text text-caption mt-2 v-messages__message"
					role="alert"
					aria-live="polite"
					data-testid="scan-type-required-error"
				>
					{{ errors[0] || 'Scan Type is required' }}
				</div>
			</validation-provider>
		</QuestionCard>
		<transition-group
			name="fade-expand"
			tag="div"
			class="scan-type-dynamic-group"
		>
			<QuestionCard
				v-if="siteScan.scanType !== null"
				:key="'type-specific'"
				:title="getScanTypeSpecificTitle"
				:description="getScanTypeSpecificDescription"
			>
				<v-row dense>
					<v-col
						v-if="siteScan.scanType === singleUrlScanType"
						cols="6"
					>
						<TextField
							:value="siteScan.domain"
							:disabled="!siteScannerFullAccess"
							label="Single URL *"
							:rules="{required:true, isUrlwithHttp:true}"
							@input="updateScanRequest('domain', $event)"
						/>
					</v-col>
					<v-col
						v-if="siteScan.scanType === sitemapUrlScanType"
						cols="6"
					>
						<TextField
							:value="siteScan.sitemapUrl"
							:disabled="!siteScannerFullAccess"
							label="Nominate your site map URL *"
							:rules="{required:true, isUrlwithHttp:true}"
							@input="updateScanRequest('sitemapUrl', $event)"
						/>
					</v-col>
					<v-col
						v-if="siteScan.scanType === sitemapFileScanType"
						cols="6"
					>
						<FileInput
							label="Upload site map XML file *"
							:disabled="!siteScannerFullAccess"
							:select-file-only="true"
							accept=".xml"
							:rules="{required: !siteScan.sitemapFile}"
							@selectFile="selectFile"
						/>
					</v-col>
					<v-col
						v-if="siteScan.scanType === indexedSitemapUrlScanType"
						cols="6"
					>
						<FileInput
							label="Upload Indexed Sitemap XML File *"
							:disabled="!siteScannerFullAccess"
							:select-file-only="true"
							accept=".xml"
							@selectFile="selectFile"
						/>
					</v-col>
				</v-row>
			</QuestionCard>
			<QuestionCard
				v-if="siteScan.scanType === singleUrlScanType"
				:key="'deep-crawl'"
				title="Would you like to limit the scan to this URL?"
				:description="limitUrlDescription"
				:show-yes-no-buttons="siteScannerFullAccess"
				:value="siteScan.deepCrawl"
				@input="updateScanRequest('deepCrawl', $event)"
			/>

			<QuestionCard
				v-if="!siteScan.deepCrawl && siteScan.scanType !== null"
				:key="'pause-period'"
				title="How many seconds should the scan pause between URLs?"
				:description="`A 'Pause Period' halts the Scanner from moving on to the next URL for the given time period. Adjust this to reduce request load on your site during the crawl.`"
			>
				<v-row dense>
					<v-col cols="6">
						<ValueSlider
							id="pause-period-slider"
							class="pause-period-slider ml-2"
							:disabled="!siteScannerFullAccess"
							max-type="Seconds"
							:min="0"
							:max="15"
							:step="1"
							ticks
							:tick-size="2"
							:thumb-label="'always'"
							:aria-label="'Pause period per URL in seconds'"
							:value="siteScan.pausePeriodPerUrl || 0"
							@input="updateScanRequest('pausePeriodPerUrl', $event)"
						/>
						<div
							class="caption mt-1"
							:aria-live="'polite'"
						>
							Current: {{ (siteScan.pausePeriodPerUrl || 0) }} second{{ (siteScan.pausePeriodPerUrl || 0) === 1 ? '' : 's' }}
							<span
								v-if="(siteScan.pausePeriodPerUrl || 0) === 0"
							>
								(no pause)
							</span>
						</div>
					</v-col>
				</v-row>
			</QuestionCard>
		</transition-group>
	</QuestionGroup>
</template>
<script>
// Dropdown import removed as the selector has been replaced by SelectorCard components
import TextField from '../../../../../../shared/components/text-field.vue'
import FileInput from '../../../../../../shared/components/file-input.vue'
import { QuestionCard, QuestionGroup, SelectorCard } from '../../../../components/component-library-wrapper.js'
import ValueSlider from '../../../../../../shared/components/value-slider.vue'

export default {
	name: 'scan-type-step',
	components: { TextField, FileInput, QuestionCard, QuestionGroup, SelectorCard, ValueSlider },
	props: {
		siteScan: { type: Object, required: true },
		updateScanRequest: { type: Function, required: true },
		scanTypeItems: { type: Array, required: true },
		siteScannerFullAccess: { type: Boolean, required: true },
		singleUrlScanType: { type: Number, required: true },
		sitemapFileScanType: { type: Number, required: true },
		sitemapUrlScanType: { type: Number, required: true },
		indexedSitemapUrlScanType: { type: Number, required: true },
		selectFile: { type: Function, required: true },
		maxUrls: { type: Number, required: true }
	},
	emits: ['update:scanType', 'update:domain', 'update:sitemapUrl', 'update:pausePeriodPerUrl', 'update:deepCrawl'],
	data () {
		return {
			isPauseSliderDragging: false
		}
	},
	computed: {
		scanTypeCards () {
			// Full list (internal) of mapped cards
			return this.scanTypeItems.map(item => {
				let description = ''
				switch (item.value) {
					case this.singleUrlScanType:
						description = 'Scan a single page. Optionally allow a deep crawl by disabling the URL limit.'
						break
					case this.sitemapFileScanType:
						description = 'Upload a sitemap XML file from your machine.'
						break
					case this.sitemapUrlScanType:
						description = 'Provide a sitemap URL and scan all listed pages.'
						break
					case this.indexedSitemapUrlScanType:
						description = 'Upload an indexed sitemap file referencing other sitemaps.'
						break
					default:
						description = ''
				}
				return { title: item.text, value: item.value, description }
			})
		},
		primaryScanTypeCards () {
			// Business requirement: display only the primary 3 options in a single row (exclude indexed sitemap for now)
			return this.scanTypeCards
				.filter(c => c.value !== this.indexedSitemapUrlScanType)
				.slice(0, 3)
		},
		getScanTypeSpecificTitle () {
			if (this.siteScan.scanType === this.singleUrlScanType) {
				return 'What URL would you like to scan?'
			}
			if (this.siteScan.scanType === this.sitemapUrlScanType) {
				return 'Provide a sitemap URL to scan its listed page'
			}
			if (this.siteScan.scanType === this.sitemapFileScanType) {
				return 'Upload a sitemap XML file and scan its listed pages'
			}
			if (this.siteScan.scanType === this.indexedSitemapUrlScanType) {
				return 'Upload an Indexed Sitemap File'
			}
			return ''
		},
		getScanTypeSpecificDescription () {
			switch (this.siteScan.scanType) {
				case this.singleUrlScanType: {
					return [
						'Scan a single page (include http:// or https://). Optionally allow deep crawl by disabling the limit toggle.'
					].join('\n')
				}
				case this.sitemapUrlScanType: {
					return [
						'Scan every page listed in the public sitemap URL.',
						'Use Indexed Sitemap if the file references other sitemaps.'
					].join('\n')
				}
				case this.sitemapFileScanType: {
					return [
						'Upload a local sitemap XML file (not publicly accessible or exported).'
					].join('\n')
				}
				case this.indexedSitemapUrlScanType: {
					return [
						'Upload an indexed sitemap file that lists other sitemap files.'
					].join('\n')
				}
				default:
					return ''
			}
		},
		limitUrlDescription () {
			// If user chooses NOT to limit (deepCrawl = false) we show the crawl limit message.
			const base = 'Disabling this option will allow the scan to deep crawl the site starting from the URL provided.'
			if (!this.siteScan.deepCrawl && this.maxUrls) {
				return `${base} This scan will crawl up to ${this.maxUrls} pages.`
			}
			return base
		}
	},
	methods: {
		selectScanType (value) {
			if (!this.siteScannerFullAccess) return
			this.updateScanRequest('scanType', value)
		},
		onPauseSliderDragStart () {
			if (!this.siteScannerFullAccess) return
			this.isPauseSliderDragging = true
		},
		onGlobalPointerUp () {
			if (this.isPauseSliderDragging) this.isPauseSliderDragging = false
		},
		getIcon (card) {
			switch (card.value) {
				case this.singleUrlScanType:
					return 'mdi-web'
				case this.sitemapFileScanType:
					return 'mdi-file-upload-outline'
				case this.sitemapUrlScanType:
					return 'mdi-file-tree'
				case this.indexedSitemapUrlScanType:
					return 'mdi-file-multiple-outline'
				default:
					return 'mdi-help-circle-outline'
			}
		}
	}
}
</script>
<style>
/* Global styles (scoped removal intentional) to ensure override of Vuetify defaults */
/* Slider: display value as a small circle centered on the track at the thumb */
.pause-period-slider {
	position: relative;
	padding-bottom: 0; /* no extra space needed now */
}
.pause-period-slider .v-slider__thumb-label,
.pause-period-slider .v-slider-thumb__label { /* Vuetify v2 & potential v3 */
	/* Position centrally over track */
	top: 50% !important;
	left: 50%;
	bottom: auto !important;
	transform: translate(-50%, -50%) !important; /* center horizontally & vertically */
	/* Circle appearance */
	width: 24px !important;
	height: 24px !important;
	border-radius: 50%;
	background: var(--v-theme-primary, var(--global-primary)) !important;
	color: #fff !important;
	display: flex;
	align-items: center;
	justify-content: center;
	font-size: 12px;
	font-weight: 600;
	line-height: 1;
	box-shadow: 0 2px 4px rgba(0,0,0,0.25);
	/* Remove default diamond rotation */
}
/* Neutralize any inner span transforms/rotations */
.pause-period-slider .v-slider__thumb-label > *,
.pause-period-slider .v-slider-thumb__label > * {
	transform: none !important;
}
/* Hide the default arrow pseudo elements if present */
.pause-period-slider .v-slider__thumb-label:before,
.pause-period-slider .v-slider__thumb-label:after,
.pause-period-slider .v-slider-thumb__label:before,
.pause-period-slider .v-slider-thumb__label:after {
	display: none !important;
}
/* Provide a centered numeric value below if thumb label somehow hidden */
.pause-period-slider[data-show-fallback]::after {
	content: attr(data-value);
	position: absolute;
	left: var(--pause-slider-fallback-left, 50%);
	bottom: 0;
	transform: translate(-50%, -4px);
	background: var(--v-theme-surface, #fff);
	padding: 2px 6px;
	font-size: 12px;
	border-radius: var(--radius-sm, 4px);
	box-shadow: 0 1px 3px rgba(0,0,0,0.25);
}
/* Scan Type cards single-row equal-height layout */
.scan-type-cards {
	display: grid;
	grid-template-columns: repeat(3, 1fr);
	grid-gap: 16px;
	align-items: stretch;
}
.scan-type-card-wrapper {
	display: flex;
}
.scan-type-card-wrapper > .selector-card {
	flex: 1 1 auto;
}
@media (max-width: 1100px) {
	.scan-type-cards { grid-template-columns: 1fr; }
}

.fade-expand-enter-active,
.fade-expand-leave-active { transition: opacity .24s ease, transform .24s ease; }
.fade-expand-enter,
.fade-expand-leave-to { opacity:0; transform:translateY(-6px); }
.fade-expand-enter-to,
.fade-expand-leave { opacity:1; transform:translateY(0); }
</style>
